import React, { useEffect, useState, useCallback } from 'react';
import SwitchWithLabel from 'components/SwitchWithLabel/SwitchWithLabel';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { contractGenI18n } from '../i18n';
import { accordionI18n } from 'components/Accordion/i18n';
import { resolveTaskType, TASK_TYPE_CONFIG } from 'components/Accordion/AccordionContent';
import { fetchTasksByService } from '../../../services/contractGeneratorService';

const StepTasks = ({ DesignSystemComponents, formData, catalogs, onChange }) => {
  const { ComboBox, Input, Table, IconButton, Spinner } = DesignSystemComponents;
  const { t } = useScopedTranslation(contractGenI18n);
  const { t: tAccordion } = useScopedTranslation(accordionI18n);

  const [continueWithoutTasks, setContinueWithoutTasks] = useState(
    Boolean(formData?.continueWithoutTasks)
  );
  const [availableTasks, setAvailableTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  const tasks = Array.isArray(formData?.tareas) ? formData.tareas : [];
  const lawyerOptions = catalogs?.lawyers || [];
  const serviceId = formData?.servicio?.value;
  const serviceName = formData?.servicio?.label || '';

  const loadTasks = useCallback(async () => {
    if (!serviceId) return;
    setLoadingTasks(true);
    try {
      const data = await fetchTasksByService(serviceId);
      const mapped = (data || [])
        .filter((t) => t.name && t.name.trim().length > 0)
        .map((t) => ({
          id: t.id,
          nombre: t.name.trim(),
          description: t.description || '',
        }));
      setAvailableTasks(mapped);
    } catch (err) {
      console.error('Error loading tasks:', err);
    } finally {
      setLoadingTasks(false);
    }
  }, [serviceId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const updateTasks = (updated) => {
    onChange({ tareas: updated });
  };

  const addTask = (option) => {
    if (!option) return;
    const taskId = option.value;
    const alreadyAdded = tasks.some((t) => t.id === taskId);
    if (alreadyAdded) return;

    const source = availableTasks.find((t) => t.id === taskId);
    if (!source) return;

    const newTask = {
      id: source.id,
      nombre: source.nombre,
      description: source.description,
      selected: true,
      asignado: null,
      fecha: '',
    };
    updateTasks([...tasks, newTask]);
  };

  const removeTask = (taskId) => {
    updateTasks(tasks.filter((t) => t.id !== taskId));
  };

  const updateTaskField = (taskId, field, value) => {
    updateTasks(
      tasks.map((t) => (t.id === taskId ? { ...t, [field]: value } : t))
    );
  };

  const addAllAvailable = (checked) => {
    if (checked) {
      const currentIds = new Set(tasks.map((t) => t.id));
      const newTasks = availableTasks
        .filter((t) => !currentIds.has(t.id))
        .map((t) => ({
          id: t.id,
          nombre: t.nombre,
          description: t.description,
          selected: true,
          asignado: null,
          fecha: '',
        }));
      updateTasks([...tasks, ...newTasks]);
    } else {
      updateTasks([]);
    }
  };

  const assignAllToLawyer = (option) => {
    if (!option) return;
    updateTasks(tasks.map((t) => ({ ...t, asignado: option })));
  };

  const assignAllDate = (date) => {
    if (!date) return;
    updateTasks(tasks.map((t) => ({ ...t, fecha: date })));
  };

  const taskOptions = availableTasks
    .filter((a) => !tasks.some((t) => t.id === a.id))
    .map((t) => ({ label: t.nombre, value: t.id }));

  const selectedCount = tasks.filter((t) => t.selected).length;

  return (
    <div className='step-content'>
      <p className='body fw-semibold'>
        {t('cg_tasks_title', { serviceName: serviceName.toUpperCase() })}
      </p>
      <p className='body mb-4'>
        {t('cg_tasks_subtitle')}
      </p>

      {loadingTasks && Spinner && <Spinner size='sm' />}

      {!loadingTasks && availableTasks.length === 0 && serviceId && (
        <p className='body color-gray-600 mb-3'>
          {t('cg_tasks_no_tasks_found')}
        </p>
      )}

      {!loadingTasks && availableTasks.length > 0 && (
        <div style={{ opacity: continueWithoutTasks ? 0.5 : 1, pointerEvents: continueWithoutTasks ? 'none' : 'auto' }}>
          <SwitchWithLabel
            variant='field'
            helperText={t('cg_tasks_add_all')}
            checked={tasks.length > 0 && tasks.length === availableTasks.length}
            onChange={addAllAvailable}
          />

          <div className='d-flex align-items-center my-3 gap-2'>
            <SwitchWithLabel
              variant='field'
              helperText={t('cg_tasks_assign_all')}
              checked={false}
              onChange={() => {}}
            />
            <ComboBox
              menuPlacement='bottom'
              onChange={assignAllToLawyer}
              options={lawyerOptions}
              placeholder={t('cg_tasks_lawyer_placeholder')}
              value={null}
              width='250px'
            />
          </div>

          <div className='d-flex align-items-center gap-2 mb-3'>
            <SwitchWithLabel
              variant='field'
              helperText={t('cg_tasks_date_all')}
              checked={false}
              onChange={() => {}}
            />
            <Input
              type='date'
              value=''
              onChange={(e) => assignAllDate(e?.target?.value || e)}
            />
          </div>

          <div className='space-like-input'>
            <ComboBox
              label={t('cg_tasks_add_tasks')}
              menuPlacement='bottom'
              onChange={addTask}
              options={taskOptions}
              placeholder={t('cg_tasks_search_placeholder')}
              value={null}
            />
          </div>

          {tasks.length > 0 && (
            <div className='table-container table-selected-tasks'>
              <Table
                columns={[
                  {
                    label: t('cg_tasks_table_task'),
                    render: (row) => {
                      const type = resolveTaskType(row);
                      const config = TASK_TYPE_CONFIG[type];
                      return (
                        <span>
                          {row.nombre}
                          {type !== 'normal' && ` (${tAccordion(config.labelKey)})`}
                        </span>
                      );
                    },
                  },
                  { accessor: 'description', label: t('cg_tasks_table_description') },
                  {
                    label: t('cg_tasks_table_assigned'),
                    render: (row) => (
                      <ComboBox
                        label=''
                        menuPlacement='bottom'
                        onChange={(opt) => updateTaskField(row.id, 'asignado', opt)}
                        options={lawyerOptions}
                        placeholder={t('cg_tasks_lawyer_placeholder')}
                        value={row.asignado}
                        width='200px'
                      />
                    ),
                  },
                  {
                    label: t('cg_tasks_table_date'),
                    render: (row) => (
                      <Input
                        name={`task-date-${row.id}`}
                        onChange={(e) => updateTaskField(row.id, 'fecha', e?.target?.value || e)}
                        type='date'
                        value={row.fecha || ''}
                        className='input-task-date'
                      />
                    ),
                  },
                  {
                    label: '',
                    render: (row) => (
                      <IconButton
                        ariaLabel={t('cg_tasks_remove_task_aria')}
                        icon='trash'
                        iconStroke='var(--error-500)'
                        size='md'
                        onClick={() => removeTask(row.id)}
                      />
                    ),
                  },
                ]}
                data={tasks}
              />
            </div>
          )}
        </div>
      )}

      <div className='d-flex justify-content-end my-4'>
        <SwitchWithLabel
          variant='field'
          label=''
          checked={continueWithoutTasks}
          onChange={(checked) => {
            setContinueWithoutTasks(checked);
            onChange({ continueWithoutTasks: checked });
          }}
          helperText={t('cg_tasks_continue_without')}
        />
      </div>

      <p className='body color-gray-600'>
        {selectedCount !== 1
          ? t('cg_tasks_selected_count_plural', { count: selectedCount })
          : t('cg_tasks_selected_count', { count: selectedCount })}
      </p>
    </div>
  );
};

export default StepTasks;
